import React from 'react';
import CloseIcon from '@mui/icons-material/Close';
import DescriptionIcon from '@mui/icons-material/Description';
import KOGInputDragDropUploader, {
  IFileExtension,
  ITypeFile
} from '../inputFile/inputDragDrop';
import { downloadFileMemory } from '@/main/services/utilsFile';
import ResultNotFoundComponent from '../resultNotFound/resultNotFoundComponent';
import KOGImage from '../kogImage/kogImage';
import GetAppIcon from '@mui/icons-material/GetApp';
import { splitExtensionOfFile } from '@/main/services/utilsString';
import * as S from './styles';
import { isNullUndefined } from '@/main/services/utils';
import { isNil } from 'lodash';
import { IconButton } from '@mui/material';

interface IFileUploaderProps {
  // required props
  files: File[];
  setFiles: React.Dispatch<React.SetStateAction<File[]>>;
  // Optional props
  maxFiles?: number;
  typeFiles?: ITypeFile[] | ITypeFile;
  maxFileByType?: { [key in IFileExtension]?: number };
  resetFiles?: boolean;
  isFullScreenDragDrop?: boolean;
  displayPreviewImages?: string;
  canUpdateFiles?: boolean;
  showMessageNotFound?: boolean;
  showPreviewImages?: boolean;
  customTextDrop?: string;
  isRequired?: boolean;
  downloadFile?: (file: File) => void;
  onCustomDropAction?: (event: React.DragEvent<HTMLDivElement>) => void;
  setImagesDelete?: React.Dispatch<React.SetStateAction<string[]>>;
  onCustomInsert?: (newFiles: File[]) => void;
  onCustomDelete?: (nameFile: string) => void;
  renderIcons?: (file: File) => React.ReactNode;
  handleClickPreviewCustom?: (index: number, urlFile: string) => void;
  maxTotalSize?: number;
  maxFileSize?: number;
}
/**
 * Component to upload files and show previews with drag and drop
 *
 * @param {File[]} files - Files to be uploaded
 * @param {number} maxFiles - Maximum number of files to be uploaded
 * @param {ITypeFile[]} typeFiles - Types of files allowed to be uploaded
 * @param {boolean} resetFiles - If true, the files will be replaced by the new ones
 * @param {boolean} isFullScreenDragDrop - If true, the drag and drop area will be full screen
 * @param {React.Dispatch<React.SetStateAction<File[]>>} setFiles - Function to set the files
 * @param {Function} onCustomDropAction - Function to be executed when the user drops a file
 * @param {string} displayPreviewImages - Types of Image Previews
 * @param {boolean} showMessageNotFound - If true, the message will be displayed when there are no files
 * @param {boolean} showPreviewImages - If true, the preview images will be shown
 * @param {Function} onCustomInsert - Function to be executed when the user inserts a file
 * @param {Function} onCustomDelete - Function to be executed when the user deletes a file
 * @example
  const [files, setFiles] = React.useState<File[]>([]);

    <KOGFileUploader
      files={files}
      setFiles={setFiles}
    />
*/

function KOGFileUploader({
  files,
  setFiles,
  typeFiles = 'default',
  maxFileByType,
  maxFiles,
  resetFiles = false,
  isFullScreenDragDrop = false,
  displayPreviewImages = 'row',
  showPreviewImages = true,
  canUpdateFiles = true,
  showMessageNotFound = false,
  isRequired = false,
  customTextDrop,
  maxTotalSize,
  maxFileSize,
  downloadFile,
  onCustomDropAction = () => {},
  setImagesDelete = () => {},
  onCustomInsert = () => {},
  onCustomDelete = () => {},
  renderIcons = () => {},
  handleClickPreviewCustom = () => {}
}: IFileUploaderProps) {
  async function handleFileChange(selectedFiles: File[]) {
    const newFiles = resetFiles
      ? selectedFiles
      : selectedFiles.filter(file => !files.some(f => f.name === file.name));
    const newFilesMemory = await Promise.all(
      newFiles.map(async file => await downloadFileMemory(file))
    );
    await onCustomInsert(newFilesMemory);
    setFiles(prev =>
      resetFiles ? selectedFiles : [...prev, ...newFilesMemory]
    );
  }

  async function handleDeleteFile(nameFile: string) {
    setImagesDelete(prevState => [...prevState, nameFile]);
    const filteredFiles = files.filter(file => file.name !== nameFile);
    await onCustomDelete(nameFile);
    setFiles(filteredFiles);
  }

  function handleClickPreview(index: number, urlFile: string, name: string) {
    if (handleClickPreviewCustom && name.toLowerCase() != 'pdf') {
      handleClickPreviewCustom(index, urlFile);
      return;
    }
    window.open(urlFile, '_blank');
  }

  const previews = files.map(file => {
    if (file.type === 'url') return file.name;
    return file.type.startsWith('image')
      ? URL.createObjectURL(file as File)
      : null;
  });

  return (
    <S.Container>
      {canUpdateFiles && (
        <KOGInputDragDropUploader
          files={files}
          typeFiles={typeFiles}
          maxFileByType={maxFileByType}
          maxFiles={maxFiles}
          resetFiles={resetFiles}
          customTextDrop={customTextDrop}
          isFullScreenDragDrop={isFullScreenDragDrop}
          onFilesChange={handleFileChange}
          onCustomDropAction={onCustomDropAction}
          maxTotalSize={maxTotalSize}
          isRequired={isRequired}
          maxFileSize={maxFileSize}
        />
      )}
      {showMessageNotFound && files.length === 0 ? (
        <ResultNotFoundComponent />
      ) : (
        <S.ContainerPreview
          previewImage={displayPreviewImages}
          id="id-general-containerPreview">
          {previews.length > 0 &&
            previews.map((previewURL, index) =>
              previewURL && showPreviewImages ? (
                <S.ItemPreview
                  key={files[index].name}
                  id="id-general-itemPreview">
                  <KOGImage
                    src={previewURL}
                    id={`id-dragDrop-img-preview-${files[index].name}`}
                    onClick={() =>
                      handleClickPreview(index, previewURL, files[index].name)
                    }
                  />
                  {canUpdateFiles && (
                    <S.ButtonDeletePreview
                      id={`id-dragDrop-btn-removePreview-${files[index].name}`}
                      onClick={() => handleDeleteFile(files[index].name)}>
                      <CloseIcon fontSize="small" />
                    </S.ButtonDeletePreview>
                  )}
                </S.ItemPreview>
              ) : (
                files.length > 0 &&
                files[index]?.name != undefined && (
                  <S.ItemFile key={files[index].name}>
                    <DescriptionIcon fontSize="small" />
                    <p title={files[index].name}>
                      <span>{splitExtensionOfFile(files[index].name)[0]}</span>
                      {splitExtensionOfFile(files[index].name)[1]}
                    </p>

                    <S.AreaIcons>
                      {!isNullUndefined(renderIcons) &&
                        renderIcons(files[index])}

                      {!isNil(downloadFile) && (
                        <IconButton onClick={() => downloadFile(files[index])}>
                          <GetAppIcon />
                        </IconButton>
                      )}
                      {canUpdateFiles && (
                        <S.ButtonDeleteFile
                          id={`id-dragDrop-btn-removePreview-${files[index].name}`}
                          onClick={() => handleDeleteFile(files[index].name)}>
                          <CloseIcon fontSize="small" />
                        </S.ButtonDeleteFile>
                      )}
                    </S.AreaIcons>
                  </S.ItemFile>
                )
              )
            )}
        </S.ContainerPreview>
      )}
    </S.Container>
  );
}

export default KOGFileUploader;
